import { Link } from "react-router-dom"
import {
  Calendar,
  Phone,
  MapPin,
  ChevronRight,
  Stethoscope,
  Baby,
  FlaskConical,
  Scissors,
  Syringe,
  Heart,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Clock,
  Shield,
  ChevronDown,
} from "lucide-react"
import SEO from "./SEO"

import { Button } from "../components/ui/button"
import { Card, CardContent, CardFooter } from "../components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel"
import { Badge } from "../components/ui/badge"

export default function MaternityMatters() {
  const maternitySchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad providing comprehensive maternity, pregnancy, and childbirth care.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/maternity-matters"
    },
    "headline": "Maternity Matters – Pregnancy & Childbirth Care at Pranaam Hospitals",
    "description": "Maternity Matters at Pranaam Hospitals provides personalized prenatal care, safe deliveries, high-risk pregnancy management, and postnatal support in Hyderabad.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "maternity care Hyderabad",
      "pregnancy hospital Hyderabad",
      "best gynecology hospital Hyderabad",
      "normal delivery hospital Hyderabad",
      "high risk pregnancy care Hyderabad"
    ]
  }
];

const galleryCarouselImages = [
  "/images/portrait-beautiful-pregnant-woman-1.jpg",
  "/images/pregnant-woman.jpg",
  "/images/portrait-newborn-baby-with-mother.jpg",
  "/images/portrait-beautiful-pregnant-woman.jpg",
]

  return (
    <>
    <SEO
  title="Maternity Matters | Safe Pregnancy & Childbirth Care at Pranaam Hospitals"
  description="Maternity Matters at Pranaam Hospitals offers expert pregnancy care, safe delivery, prenatal & postnatal services with experienced gynecologists in Hyderabad."
  canonical="https://pranaam.pranaamhospitals.in/maternity-matters"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Maternity Matters Pranaam Hospitals, maternity hospital Hyderabad,
            pregnancy care Hyderabad, best gynecologist hospital Hyderabad,
            normal delivery hospital Hyderabad, high risk pregnancy care,
            prenatal care Hyderabad, postnatal care Hyderabad"
  schema={maternitySchema}
/>



    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section with Full-width Image */}
        <section className="relative h-[80vh] min-h-[500px] flex items-center">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/MaternitymatersBanner.jpg  "
              alt="Maternity Care"
              className="w-full h-full object-cover brightness-50"
            />
          </div>
          <div className="container relative z-10 px-4 mx-auto text-white">
            <div className="max-w-2xl">
              <Badge className="mb-4 bg-white/20 text-white border-white/30">
                <Sparkles className="w-4 h-4 mr-2" />
                Maternity Matters
              </Badge>
              <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Your Partner in <span className="text-primary">Women's Health</span>
              </h1>
              <p className="max-w-md mt-6 text-lg text-white/90">
                At Pranaam Hospitals, we are dedicated to providing compassionate, expert care at every stage of a
                woman's life. From adolescence to motherhood to menopause, our team is here for you.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Button
                  size="lg"
                  className="gap-2 group transition-all duration-300 hover:translate-y-[-2px] shadow-lg bg-white text-primary hover:bg-white/90">
                   <Calendar className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    <a
                    href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                    rel="noopener noreferrer"
                  >
                  Book an Appointment</a>
                  </Button>
                {/* <Button
                  size="lg"
                  className="gap-2 group transition-all duration-300 hover:translate-y-[-2px] shadow-lg bg-white text-primary hover:bg-white/90"
                >
                  <MapPin className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Visit Us Today
                </Button> */}
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 flex justify-center">
            <a href="#maternity-matters"
              className="flex items-center justify-center w-10 h-10 mb-8 text-white bg-primary/50 rounded-full backdrop-blur-sm animate-bounce"
            >
              <ChevronDown className="w-6 h-6" />
              <span className="sr-only">Scroll Down</span>
            </a>
          </div>
        </section>

        {/* Maternity Matters Highlight Section */}
        <section id="maternity-matters" className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-10 text-center">
              <Badge className="mb-4 bg-primary/10 text-primary">
                <Baby className="w-5 h-5 mr-2" />
                Our Signature Program
              </Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Maternity <span className="text-primary">Matters</span>
              </h2>
              <p className="mb-6 text-xl italic text-muted-foreground">
                Because Every Mother Deserves Exceptional Care
              </p>
              <p className="max-w-3xl text-lg text-muted-foreground">
                Our "Maternity Matters" program is thoughtfully designed to offer personalized, holistic support to
                expectant mothers and their families. We believe that motherhood is a beautiful journey – and we're here
                for you every step of the way.
              </p>
            </div>

            {/* Image Gallery */}
            <div className="mt-12 mb-16">
              <Carousel className="w-full">
               <CarouselContent>
                  {galleryCarouselImages.map((src, index) => (
                    <CarouselItem
                      key={index}
                      className="basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/3"
                    >
                      <div className="p-1">
                        <div className="overflow-hidden rounded-xl">
                          <img
                            src={src}
                            alt={`Maternity Care ${index + 1}`}
                            className="w-full h-[20rem] object-cover transition-transform duration-500 hover:scale-105"
                          />
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="flex justify-center mt-4">
                  <CarouselPrevious className="relative mr-2 left-0" />
                  <CarouselNext className="relative ml-2 right-0" />
                </div>
              </Carousel>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-12 sm:grid-cols-3">
              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img
                    src="/images/realistic-scene-with-elderly-care-senior-people.jpg"
                    alt="Comprehensive Care"
                    className="w-full h-full object-cover"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Comprehensive Care</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Regular antenatal checkups, personalized birth plans, and 24x7 emergency obstetric care.
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img
                    src="/images/african-american-assistant-discussing-childbirth-process-with-pregnant-woman-preparing-mother-surgery-hospital-ward-patient-with-pregnancy-lying-bed-waiting-gynecologist-doctor.jpg"
                    alt="Delivery Options"
                    className="w-full h-full object-cover"
                  />
            
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Delivery Options</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Normal, painless epidural, cesarean, and VBAC deliveries with expert medical supervision.
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-80">
                  <img
                    src="/images/women-holding-babies-carriers-side-view.jpg"
                    alt="Holistic Support"
                    className="w-full h-full object-cover"
                  />
             
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Holistic Support</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-muted-foreground">
                    Nutrition planning, breastfeeding guidance, and emotional well-being support throughout your
                    journey.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="p-6 mt-12 rounded-2xl bg-gray-100">
              <h3 className="mb-4 text-xl font-bold">Highlights of Maternity Matters:</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[
                  "Comprehensive antenatal checkups & scans",
                  "Personalized birth plans",
                  "Pain management options including epidural delivery",
                  "VBAC counseling and delivery support",
                  "Regular educational sessions and birthing classes",
                  "Nutrition & wellness planning during pregnancy",
                  "24x7 emergency obstetric care",
                  "Breastfeeding guidance & lactation consultation",
                  "Emotional well-being support, including postpartum blues care",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 text-primary shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-lg font-medium text-center">
                Whether it's your first baby or your third, our team ensures you feel empowered, informed, and cared for
                – every single day.
              </p>
            </div>

            <div className="flex justify-center mt-10">
              <Button size="lg" className="gap-2 shadow-md" asChild>
               <a
                href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                rel="noopener noreferrer"
              >
                  Begin Your Journey With Us
                  <ArrowRight className="w-4 h-4 ml-1" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Visual Journey Section */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-4 bg-white text-primary">
                <Clock className="w-5 h-5 mr-2" />
                Your Journey With Us
              </Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                From <span className="text-primary">Conception</span> to{" "}
                <span className="text-primary">Cradle</span>
              </h2>
              <p className="max-w-2xl text-lg text-muted-foreground">
                We're with you at every milestone of your pregnancy journey
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: "First Trimester",
                  image: "/images/FirstTrimester.png",
                  description:
                    "Comprehensive initial checkups, genetic counseling, and nutritional guidance to start your journey right.",
                  items: [
                    "Confirmation of pregnancy",
                    "Dating scan",
                    "Genetic screening options",
                    "Nutrition planning",
                  ],
                },
                {
                  title: "Second Trimester",
                  image: "/images/SecondTrimester.png",
                  description:
                    "Regular monitoring, anomaly scans, and preparation classes to ensure both you and your baby are thriving.",
                  items: [
                    "Detailed anomaly scan",
                    "Regular growth monitoring",
                    "Birthing classes begin",
                    "Pregnancy yoga sessions",
                  ],
                },
                {
                  title: "Third Trimester",
                  image: "/images/ThirdTrimester.png",
                  description:
                    "Close monitoring, birth plan finalization, and preparation for the arrival of your little one.",
                  items: [
                    "Weekly checkups",
                    "Birth plan finalization",
                    "Hospital tour",
                    "Breastfeeding guidance",
                    "Postnatal care planning",
                  ],
                },
              ].map((phase, index) => (
                <div key={index} className="flex flex-col h-full">
                  <div className="relative h-64 mb-4 overflow-hidden rounded-xl">
                    <img src={phase.image} alt={phase.title} className="w-full h-full object-cover" />
                  </div>
                  
                 
                  
                  <h3 className="mb-2 text-xl font-bold">{phase.title}</h3>
                  <p className="mb-4 text-muted-foreground">{phase.description}</p>
                  <ul className="pl-5 mt-auto space-y-1 list-disc">
                    {phase.items.map((item, idx) => (
                      <li key={idx} className="text-sm">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section with Visual Tabs */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-4 bg-gray-100 text-primary">
                <Stethoscope className="w-5 h-5 mr-2" />
                Our Services
              </Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Comprehensive <span className="text-primary">Women's Healthcare</span>
              </h2>
              <p className="max-w-2xl text-lg text-muted-foreground">
                We offer a wide range of specialized services to address all your women's health needs with compassion
                and expertise.
              </p>
            </div>

            <Tabs defaultValue="gynecology" className="w-full max-w-5xl mx-auto">
              <TabsList className="grid w-full grid-cols-2 mb-8 md:grid-cols-3 lg:grid-cols-2">
                <TabsTrigger value="gynecology">Gynecology</TabsTrigger>
                <TabsTrigger value="pregnancy">Pregnancy</TabsTrigger>
                {/* <TabsTrigger value="fertility">Fertility</TabsTrigger>
                <TabsTrigger value="surgeries">Surgeries</TabsTrigger>
                <TabsTrigger value="screening">Screening</TabsTrigger>
                <TabsTrigger value="wellness">Wellness</TabsTrigger> */}
              </TabsList>

              {/* General Gynecology */}
              <TabsContent value="gynecology" className="overflow-hidden rounded-2xl shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative h-full min-h-[300px]">
                    <img
                      src="/images/GeneralGynecology.png"
                      alt="Gynecology Services"
                      className="w-full h-full object-cover"
                    />
                    
                  </div>
                  <div className="p-8 bg-white">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 rounded-full bg-gray-100">
                        <Stethoscope className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold">General Gynecology</h3>
                    </div>
                    <p className="mb-6 text-lg">
                      We address a wide range of women's health concerns with personalized care:
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        "Irregular or painful periods",
                        "Prolonged periods",
                        "PMS (Premenstrual Syndrome)",
                        "PCOD/PCOS management",
                        "Fibroid & ovarian cyst treatment",
                        "Endometriosis care",
                        "Menopause management",
                        "Family planning & contraception",
                        "Pre-marital counseling",
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 mt-1 text-primary shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Pregnancy & Maternity Care */}
              <TabsContent value="pregnancy" className="overflow-hidden rounded-2xl shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="relative h-full min-h-[300px]">
                    <img
                      src="/images/portrait-beautiful-pregnant-woman.jpg"
                      alt="Pregnancy Care"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 bg-white">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 rounded-full bg-gray-100">
                        <Baby className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold">Pregnancy & Maternity Care</h3>
                    </div>
                    <p className="mb-6 text-lg">
                      Your pregnancy journey deserves gentle hands and expert care. We offer:
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        "Pre-pregnancy counseling",
                        "Antenatal check-ups & monitoring",
                        "High-risk pregnancy care",
                        "Ultrasound scans and tests",
                        "Normal & Cesarean deliveries",
                        "Painless (Epidural) Delivery",
                        "VBAC options",
                        "Postnatal recovery support",
                        "Breastfeeding support",
                      ].map((item, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 mt-1 text-primary shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Other tabs would follow similar pattern... */}
            </Tabs>
          </div>
        </section>

        {/* Facilities Gallery */}
        <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-white text-brand-magenta">
                <Shield className="w-5 h-5" />
                <span>Our Facilities</span>
              </div>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                State-of-the-Art <span className="text-brand-magenta">Facilities</span>
              </h2>
              <p className="max-w-2xl text-lg text-muted-foreground">
                Experience world-class care in our modern, comfortable facilities designed with your needs in mind
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  title: "Labor & Delivery Rooms",
                  image: "/images/Labor&DeliveryRooms.png",
                },
                {
                  title: "Neonatal Care Unit",
                  image: "/images/NeonatalCareUnit.png",
                },
                {
                  title: "Consultation Suites",
                  image: "/images/ConsultationSuites.png",
                },
                {
                  title: "Ultrasound & Imaging",
                  image: "/images/Ultrasound&Imaging.png",
                },
                {
                  title: "Surgical Theaters",
                  image: "/images/SurgicalTheaters.png",
                },
                {
                  title: "Recovery Rooms",
                  image: "/images/RecoveryRooms.png",
                },
                {
                  title: "Birthing Classes",
                  image: "/images/BirthingClasses.png",
                },
                {
                  title: "Lactation Support Center",
                  image: "/images/LactationSupportCenter.png",
                },
              ].map((facility, index) => (
                <div key={index} className="group">
                  <div className="overflow-hidden rounded-xl">
                    <div className="relative h-64">
                      <img
                        src={facility.image || "/placeholder.svg"}
                        alt={facility.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                   
                      <div className="absolute inset-0 transition-opacity bg-black/30 group-hover:opacity-0"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <h3 className="px-4 py-2 text-lg font-bold text-white transition-all duration-300 bg-brand-magenta/80 rounded-md group-hover:bg-brand-magenta">
                          {facility.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Us Section with Image */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-2xl shadow-xl md:h-96">
                  <img
                    src="/images/WomensHealthcareTeam.png"
                    alt="Women's Healthcare Team"
                    fill
                    className="object-cover"
                  />
             
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-magenta/10 rounded-full"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-brand-magenta/10 rounded-full"></div>
              </div>
              <div>
                <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-gray-100 text-brand-magenta">
                  <Heart className="w-5 h-5" />
                  <span>About Us</span>
                </div>
                <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
                  Compassion. <span className="text-brand-magenta">Expertise.</span> Innovation.
                </h2>
                <p className="mb-8 text-lg text-muted-foreground">
                  At Pranaam Hospitals, our mission is to redefine women's healthcare with love, empathy, and
                  excellence. With state-of-the-art facilities, expert doctors, and a patient-first approach, we are
                  your trusted healthcare partner for every phase of life.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button className="gap-2 shadow-md bg-brand-magenta hover:bg-brand-magenta/90" asChild>
                    <Link to="/about">    
                      Learn More About Us
                      <ChevronRight className="w-4 h-4" />
                    </Link>  
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2 border-brand-magenta text-brand-magenta hover:bg-brand-magenta hover:text-white"
                    asChild
                  >
                    <Link to="/doctors">
                      Meet Our Doctors
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section with Visual Cards */}
        {/* <section className="py-16 bg-gray-100">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 mb-4 space-x-2 text-sm font-medium rounded-full bg-white text-brand-magenta">
                <Phone className="w-5 h-5" />
                <span>Get in Touch</span>
              </div>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl">
                Contact <span className="text-brand-magenta">Us</span>
              </h2>
              <p className="max-w-2xl text-lg text-muted-foreground">
                Have questions or want to schedule an appointment? Reach out to us through any of these channels.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4">
              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Call+Us"
                    alt="Call Us"
                    fill
                    className="object-cover"
                  />
                  <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Call Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">1800-123-4567</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="tel:18001234567">Call Now</Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Visit+Us"
                    alt="Visit Us"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Visit Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">123 Healthcare Avenue, Medical District, City - 400001</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="https://maps.google.com" target="_blank">
                      Get Directions
                    </Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Email+Us"
                    alt="Email Us"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Email Us</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">info@pranaamhospitals.com</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link href="mailto:info@pranaamhospitals.com">Send Email</Link>
                  </Button>
                </CardFooter>
              </Card>

              <Card className="overflow-hidden transition-all border-none shadow-lg hover:shadow-xl hover:translate-y-[-5px] duration-300">
                <div className="relative h-40">
                  <img
                    src="/placeholder.svg?height=300&width=500&text=Book+Online"
                    alt="Book Online"
                    fill
                    className="object-cover"
                  />
                   <div className="absolute top-[110%] right-[42%]">
                            <h1>324 × 324 px</h1>
                          </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white">Book Online</h3>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground">Schedule your appointment online</p>
                </CardContent>
                <CardFooter className="p-4 pt-0 justify-center">
                  <Button variant="ghost" size="sm" className="text-brand-magenta" asChild>
                    <Link to="/book-appointment">Schedule Now</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section> */}

        {/* CTA Section with Background Image */}
        {/* <section className="relative py-20 text-white">
          <div className="absolute inset-0 z-0">
            <img
              src="/placeholder.svg?height=800&width=1600&text=Ready+To+Begin"
              alt="Ready To Begin"
              className="w-full h-full object-cover brightness-50"
            />
          </div>
          <div className="container relative z-10 px-4 mx-auto">
            <div className="flex flex-col items-center text-center">
              <h2 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">Ready to Schedule Your Visit?</h2>
              <p className="max-w-2xl mb-8 text-lg opacity-90">
                Our team of specialists is ready to provide you with the care and support you deserve.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white"
                  asChild
                >
                  <Link to="/book-appointment">
                    <Calendar className="w-5 h-5" />
                    Book an Appointment
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  className="gap-2 shadow-md bg-primary hover:bg-primary/90 text-white"
                  asChild
                >
                  <Link to="tel:+919966060000">
                    <Phone className="w-5 h-5" />
                    Call Us Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section> */}
      </main>
    </div>
    </>
  )
}